/**
 * Content Block TOC
 * Sticky scroll-spy for buying-guide content. Reveals desktop right rail
 * and mobile jump-to pill, both keyed by heading IDs in the article body.
 * Active state is driven by a passive, rAF-throttled scroll listener rather
 * than an IntersectionObserver, so hash jumps, TOC clicks, and fast scrolls
 * can't leave the active label/rail highlight stale.
 *
 * Progressive enhancement: when this script runs, it sets
 * [data-toc-enhanced] on the .buying-guide root, which the CSS uses to
 * hide the inline accordion and reveal the sticky variants. With JS off,
 * users still get the inline accordion fallback.
 */

if (typeof window.ContentBlockToc === 'undefined') {
  window.ContentBlockToc = class ContentBlockToc {
    constructor(root) {
      this.root = root;
      this.body = root.querySelector('.buying-guide__body');
      this.rail = root.querySelector('[data-toc-rail]');
      this.pill = root.querySelector('[data-toc-pill]');

      if (!this.body || (!this.rail && !this.pill)) return;

      this.railLinks = this.rail
        ? Array.from(this.rail.querySelectorAll('[data-toc-target]'))
        : [];
      this.pillLabel = this.pill
        ? this.pill.querySelector('[data-toc-pill-label]')
        : null;
      this.pillToggle = this.pill
        ? this.pill.querySelector('[data-toc-pill-toggle]')
        : null;
      this.pillSheet = this.pill
        ? this.pill.querySelector('[data-toc-pill-sheet]')
        : null;
      this.pillSheetLinks = this.pillSheet
        ? Array.from(this.pillSheet.querySelectorAll('[data-toc-target]'))
        : [];

      // Track only headings represented in the TOC. Body-only headings (H3
      // subsections, or H2s the renderer suppresses like "Introduction" and
      // "Conclusion") would otherwise drive activeId to an id with no rail
      // link, clearing every link's data-active and making the highlight
      // disappear as the user scrolls through those sections.
      const tocIds = new Set();
      this.railLinks.forEach((l) => tocIds.add(l.dataset.tocTarget));
      this.pillSheetLinks.forEach((l) => tocIds.add(l.dataset.tocTarget));

      this.headings = Array.from(
        this.body.querySelectorAll('.content-block--heading[id]')
      ).filter((h) => tocIds.has(h.id));
      if (this.headings.length === 0) return;

      this.activeId = null;
      this.headerOffsetPx = 0;
      this.abortController = new AbortController();
      this.observers = [];

      this.init();

      // Cache instance on the root element so the section-lifecycle handler
      // can find and destroy it on unload.
      this.root._contentBlockToc = this;
    }

    init() {
      this.root.dataset.tocEnhanced = '';
      this.measureHeaderOffset();
      this.bindLinks();
      this.bindPill();
      this.bindResize();
      this.bindLoad();
      this.bindScroll();
      this.observePillVisibility();
      this.seedFromHash();
      // Compute initial active state from current scroll position.
      this.updateActiveFromScroll();
    }

    bindLoad() {
      // header.js measures and sets --header-height after layout, which may
      // happen after our DOMContentLoaded init. Re-measure on window.load
      // so subsequent scroll-spy and link clicks use the correct offset.
      window.addEventListener(
        'load',
        () => {
          const previous = this.headerOffsetPx;
          this.measureHeaderOffset();
          if (this.headerOffsetPx !== previous) {
            this.updateActiveFromScroll();
          }
        },
        { signal: this.abortController.signal, once: true }
      );
    }

    /** Resolve --header-height to a pixel value. */
    measureHeaderOffset() {
      const raw = getComputedStyle(document.documentElement)
        .getPropertyValue('--header-height')
        .trim();

      // Fast path: px (the unit header.js writes).
      if (raw.endsWith('px')) {
        const num = parseFloat(raw);
        if (Number.isFinite(num)) {
          this.headerOffsetPx = num;
          return;
        }
      }

      // Fallback for any other unit: render the value on a probe element
      // and read its computed height.
      if (raw) {
        const probe = document.createElement('div');
        probe.style.position = 'absolute';
        probe.style.visibility = 'hidden';
        probe.style.pointerEvents = 'none';
        probe.style.height = raw;
        document.body.appendChild(probe);
        const px = probe.offsetHeight;
        probe.remove();
        if (px > 0) {
          this.headerOffsetPx = px;
          return;
        }
      }

      // Final fallback when --header-height is unset or unreadable.
      this.headerOffsetPx = 80;
    }

    bindResize() {
      // Header height can change at responsive breakpoints; refresh the
      // offset and recompute the active heading when it does.
      let resizeTimer = null;
      window.addEventListener(
        'resize',
        () => {
          if (resizeTimer) clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => {
            const previous = this.headerOffsetPx;
            this.measureHeaderOffset();
            if (this.headerOffsetPx !== previous) {
              this.updateActiveFromScroll();
            }
          }, 150);
        },
        { signal: this.abortController.signal }
      );
    }

    bindLinks() {
      const allLinks = this.root.querySelectorAll('[data-toc-target]');
      allLinks.forEach((link) => {
        link.addEventListener('click', (e) => this.onLinkClick(e, link), {
          signal: this.abortController.signal,
        });
      });
    }

    bindPill() {
      if (!this.pill || !this.pillToggle || !this.pillSheet) return;

      this.pillToggle.addEventListener(
        'click',
        () => this.togglePillSheet(),
        { signal: this.abortController.signal }
      );

      document.addEventListener(
        'click',
        (e) => {
          if (this.isPillOpen() && !this.pill.contains(e.target)) {
            this.closePillSheet();
          }
        },
        { signal: this.abortController.signal }
      );

      document.addEventListener(
        'keydown',
        (e) => {
          if (e.key === 'Escape' && this.isPillOpen()) {
            this.closePillSheet();
            this.pillToggle.focus();
          }
        },
        { signal: this.abortController.signal }
      );

      this.pill.addEventListener(
        'focusout',
        (e) => {
          if (this.isPillOpen() && !this.pill.contains(e.relatedTarget)) {
            this.closePillSheet();
          }
        },
        { signal: this.abortController.signal }
      );
    }

    bindScroll() {
      let ticking = false;
      window.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            this.updateActiveFromScroll();
            ticking = false;
          });
        },
        { passive: true, signal: this.abortController.signal }
      );
    }

    observePillVisibility() {
      // Mobile pill shows only while the reader is inside the article: after
      // the header has scrolled out of view, and before the article itself
      // has. Watching the header alone left the pill parked over the footer
      // for the whole scroll past it, covering footer links.
      if (!this.pill) return;
      const header = this.root.querySelector('.buying-guide__header');
      const sentinel = header || this.headings[0];
      if (!sentinel) return;

      this.headerPassed = false;
      this.articleInView = true;

      const headerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.headerPassed =
              !entry.isIntersecting && entry.boundingClientRect.top < 0;
          });
          this.syncPillVisibility();
        },
        { threshold: 0 }
      );
      headerObserver.observe(sentinel);
      this.observers.push(headerObserver);

      const articleObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.articleInView = entry.isIntersecting;
          });
          this.syncPillVisibility();
        },
        { threshold: 0 }
      );
      articleObserver.observe(this.root);
      this.observers.push(articleObserver);
    }

    syncPillVisibility() {
      if (!this.pill) return;
      if (this.headerPassed && this.articleInView) {
        this.pill.dataset.visible = '';
      } else {
        delete this.pill.dataset.visible;
        this.closePillSheet();
      }
    }

    /** If the URL has a hash matching a heading, prime the active state. */
    seedFromHash() {
      const hash = window.location.hash.slice(1);
      if (!hash) return;
      const match = this.headings.find((h) => h.id === hash);
      if (match) this.setActive(hash);
    }

    /** Pick the last heading whose top has scrolled past the active band. */
    updateActiveFromScroll() {
      if (this.headings.length === 0) return;
      const threshold = this.headerOffsetPx + 80;
      let activeId = this.headings[0].id;
      for (const h of this.headings) {
        if (h.getBoundingClientRect().top <= threshold) {
          activeId = h.id;
        } else {
          break;
        }
      }
      // At the very bottom of the page, activate the last section even when
      // its heading is too close to the end to ever cross the active band.
      if (
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 2
      ) {
        activeId = this.headings[this.headings.length - 1].id;
      }
      this.setActive(activeId);
    }

    setActive(id) {
      if (id === this.activeId) return;
      this.activeId = id;

      let activeRailLink = null;
      this.railLinks.forEach((link) => {
        if (link.dataset.tocTarget === id) {
          link.dataset.active = '';
          link.setAttribute('aria-current', 'location');
          activeRailLink = link;
        } else {
          delete link.dataset.active;
          link.removeAttribute('aria-current');
        }
      });

      if (activeRailLink) this.scrollRailToActive(activeRailLink);

      this.pillSheetLinks.forEach((link) => {
        if (link.dataset.tocTarget === id) {
          link.setAttribute('aria-current', 'location');
        } else {
          link.removeAttribute('aria-current');
        }
      });

      if (this.pillLabel) {
        const heading = document.getElementById(id);
        if (heading) {
          const inner = heading.querySelector('h2, h3');
          this.pillLabel.textContent = inner
            ? inner.textContent.trim()
            : heading.textContent.trim();
        }
      }
    }

    /**
     * Keep the active link visible inside the rail's internal scroll region.
     * Only nudges rail.scrollTop — never calls scrollIntoView, which would
     * bubble up and scroll the page itself.
     */
    scrollRailToActive(link) {
      if (!this.rail) return;
      if (this.rail.scrollHeight <= this.rail.clientHeight) return;

      const railRect = this.rail.getBoundingClientRect();
      const linkRect = link.getBoundingClientRect();
      const padding = 24;

      if (linkRect.top < railRect.top + padding) {
        this.rail.scrollTop += linkRect.top - railRect.top - padding;
      } else if (linkRect.bottom > railRect.bottom - padding) {
        this.rail.scrollTop += linkRect.bottom - railRect.bottom + padding;
      }
    }

    onLinkClick(e, link) {
      const id = link.dataset.tocTarget;
      const target = document.getElementById(id);
      if (!target) return;

      e.preventDefault();

      const reduced = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
      const top =
        target.getBoundingClientRect().top +
        window.scrollY -
        this.headerOffsetPx -
        16;

      window.scrollTo({
        top,
        behavior: reduced ? 'auto' : 'smooth',
      });

      if (history.replaceState) {
        history.replaceState(null, '', `#${id}`);
      }

      this.setActive(id);
      this.closePillSheet();

      // Move focus to the heading so screen readers and keyboard users land
      // in the right context. preventScroll avoids fighting the smooth scroll.
      try {
        target.focus({ preventScroll: true });
      } catch (_e) {
        // Older browsers without focus options — ignore.
      }
    }

    togglePillSheet() {
      if (this.isPillOpen()) {
        this.closePillSheet();
      } else {
        this.openPillSheet();
      }
    }

    openPillSheet() {
      if (!this.pill || !this.pillSheet || !this.pillToggle) return;
      this.pill.dataset.open = '';
      this.pillSheet.removeAttribute('hidden');
      this.pillToggle.setAttribute('aria-expanded', 'true');

      // Move focus to the first link in the sheet for keyboard users.
      const first = this.pillSheet.querySelector('a');
      if (first) first.focus();
    }

    closePillSheet() {
      if (!this.pill || !this.pillSheet || !this.pillToggle) return;
      delete this.pill.dataset.open;
      this.pillSheet.setAttribute('hidden', '');
      this.pillToggle.setAttribute('aria-expanded', 'false');
    }

    isPillOpen() {
      return this.pill && this.pill.dataset.open === '';
    }

    destroy() {
      this.abortController.abort();
      this.observers.forEach((o) => o.disconnect());
      this.observers = [];
      delete this.root.dataset.tocEnhanced;
      delete this.root._contentBlockToc;
    }
  };

  const initRoot = (root) => {
    if (root._contentBlockToc) return;
    new window.ContentBlockToc(root);
  };

  const initAll = () => {
    document.querySelectorAll('[data-buying-guide]').forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor: re-init / clean up on section lifecycle events.
  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector('[data-buying-guide]');
    if (root) initRoot(root);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const root = e.target.querySelector('[data-buying-guide]');
    if (root && root._contentBlockToc) root._contentBlockToc.destroy();
  });
}
